import { useCallback, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Activity, Radio } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Pill } from '@/components/widgets/Pill';
import { adminAudit } from '@/api/admin';
import { useChannel } from '@/realtime/use-channel';
import type { Money, Signal } from '@/api/types';
import { cn } from '@/lib/utils';
import { fmtMoney, fmtNum, fmtTime, moneyToNumber } from '@/lib/format';
import { SignalDetailDialog } from '@/features/signals/SignalDetailDialog';

const STATUS_LABEL: Record<Signal['status'], string> = {
  executed: '成交',
  skipped_user_status: '停用',
  skipped_risk: '风控',
  skipped_session: '时段',
  failed: '失败',
};

const STATUS_TONE: Record<Signal['status'], 'up' | 'down' | 'warn' | 'neutral'> = {
  executed: 'up',
  skipped_user_status: 'neutral',
  skipped_risk: 'warn',
  skipped_session: 'warn',
  failed: 'down',
};

/**
 * 当日跟单流水 — 资金账户大屏底部滚动列表。
 * 拉取近 1h 的 signals + 订阅 WS 实时追加。
 */
export function RecentSignalsStream() {
  const fromIso = useMemo(() => new Date(Date.now() - 3600_000).toISOString(), []);
  const listQ = useQuery({
    queryKey: ['admin', 'signals', 'live-screen-recent'],
    queryFn: () => adminAudit.signals({ pageSize: 30, from: fromIso }),
    refetchInterval: 15_000,
  });

  const [realtime, setRealtime] = useState<Signal[]>([]);
  const [selected, setSelected] = useState<Signal | null>(null);
  const onSig = useCallback((s: Signal) => {
    setRealtime((prev) => (prev.some((p) => p.id === s.id) ? prev : [s, ...prev].slice(0, 15)));
  }, []);
  useChannel('signals', onSig, [onSig]);

  const items = useMemo(() => {
    const ids = new Set(realtime.map((s) => s.id));
    return [...realtime, ...(listQ.data?.items ?? []).filter((s) => !ids.has(s.id))].slice(0, 30);
  }, [realtime, listQ.data]);

  const lots = (n: number) => (n / 100).toFixed(2);

  return (
    <>
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-brand" />
            当日跟单流水 · {items.length} 条
          </span>
          <Pill tone="up" className="text-xs">
            <Radio className="h-3 w-3 animate-pulse-glow" />
            实时
          </Pill>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="max-h-72 overflow-y-auto overflow-x-auto scrollbar-thin">
          <table className="w-full text-xs whitespace-nowrap">
            <thead className="bg-bg-3 text-ink-2 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-normal">时间</th>
                <th className="px-3 py-2 text-left font-normal">用户ID</th>
                <th className="px-3 py-2 text-left font-normal">雇员单</th>
                <th className="px-3 py-2 text-right font-normal">用户入场价</th>
                <th className="px-3 py-2 text-right font-normal">平仓价</th>
                <th className="px-3 py-2 text-right font-normal">盈亏</th>
                <th className="px-3 py-2 text-right font-normal">手续费</th>
                <th className="px-3 py-2 text-right font-normal">净盈亏</th>
                <th className="px-3 py-2 text-left font-normal">→ 主账户净额贡献</th>
                <th className="px-3 py-2 text-left font-normal">模式·比例</th>
                <th className="px-3 py-2 text-left font-normal">状态</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s, i) => (
                <tr
                  key={s.id}
                  className={cn('cursor-pointer border-t border-line row-hover', i < realtime.length && 'animate-flash')}
                  onClick={() => setSelected(s)}
                >
                  <td className="px-3 py-1.5 num text-ink-2">{fmtTime(s.createdAt)}</td>
                  <td className="px-3 py-1.5 num text-ink-1">{s.userId}</td>
                  <td className="px-3 py-1.5">
                    <Pill tone={s.userSide === 'BUY' ? 'up' : 'down'}>{s.userSide}</Pill>
                    <span className="ml-1.5 num">{lots(s.userVolume)}</span>
                  </td>
                  <td className="px-3 py-1.5 num text-right text-ink-1">{priceCell(s.userEntryPrice)}</td>
                  <td className="px-3 py-1.5 num text-right text-ink-1">{priceCell(s.userClosePrice)}</td>
                  <td className="px-3 py-1.5 num text-right">{moneyCell(s.userGrossPnl)}</td>
                  <td className="px-3 py-1.5 num text-right">{moneyCell(s.userServiceFee, false, 'neutral')}</td>
                  <td
                    className="px-3 py-1.5 num text-right"
                    title={s.userFeeAllocation ? `费用分摊 ${fmtMoney(s.userFeeAllocation, { sign: true })}` : undefined}
                  >
                    {moneyCell(s.userNetPnl, true)}
                  </td>
                  <td className="px-3 py-1.5 text-ink-2">
                    {s.realSide ? (
                      <>
                        <Pill tone={s.realSide === 'BUY' ? 'up' : 'down'}>{s.realSide}</Pill>
                        <span className="ml-1.5 num">{s.realVolume ? lots(s.realVolume) : '—'}</span>
                      </>
                    ) : (
                      <span className="text-ink-3">—</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    <span className="text-xs">{s.followMode}</span>
                    <span className="num text-ink-3 ml-1.5">×{s.followRatioUsed?.toFixed(2) ?? '—'}</span>
                  </td>
                  <td className="px-3 py-1.5">
                    <Pill tone={STATUS_TONE[s.status]}>{STATUS_LABEL[s.status]}</Pill>
                  </td>
                </tr>
              ))}
              {items.length === 0 && !listQ.isLoading && (
                <tr>
                  <td colSpan={11} className="px-3 py-6 text-center text-ink-3">
                    近 1 小时无跟单流水
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-3 py-2 text-[11px] leading-relaxed text-ink-3 border-t border-line">
          「主账户净额贡献」为该单按模式·比例投影到主账户的净额贡献(R=round(v·r));平仓价 / 盈亏 / 手续费 /
          净盈亏为 App 用户结算口径,未结算时显示为空。
        </div>
      </CardContent>
    </Card>
    <SignalDetailDialog signal={selected} onClose={() => setSelected(null)} />
    </>
  );
}

/** 价格单元格(用户入场价);无值显示灰色 — */
function priceCell(price: number | null | undefined) {
  if (price == null) return <span className="text-ink-3">—</span>;
  return fmtNum(price, 2);
}

function moneyCell(m: Money | null | undefined, emphasized = false, toneMode: 'pnl' | 'neutral' = 'pnl') {
  if (!m || m.value == null) return <span className="text-ink-3">—</span>;
  const n = moneyToNumber(m);
  const tone = toneMode === 'neutral' ? 'text-ink-1' : n > 0 ? 'text-up' : n < 0 ? 'text-down' : 'text-ink-1';
  return <span className={`${tone} ${emphasized ? 'font-medium' : ''}`}>{fmtMoney(m, { sign: toneMode !== 'neutral' })}</span>;
}
